/**
 * 模板里所有「字段引用」的绑定体检。
 *
 * 为什么需要：模板里的字段可以按 id 绑，也可以按名字绑（模板库、AI 生成、
 * 手写的 {{占位符}} 都是按名字）。字段被改名或删掉之后，引用就落空了 ——
 * 而落空的表现是**打印出来那一格是空白**，界面上没有任何提示。用户只会觉得
 * 「这个插件漏字段」，根本不知道去哪儿改。
 *
 * 这里把每一处引用连同它所在的位置一起列出来，界面据此显示
 * 「已绑定 N / 未匹配 M」并能逐个定位，和渲染时用的是同一套匹配规则
 * （findFieldByRef），所以这里说绑上了，打印时就一定取得到值。
 */
import { walkSections } from './sectionTree';
import { findFieldByRef } from './fieldMatch';
import type { ContractSection, ContractTemplate, FieldMeta } from './types';

export interface FieldBinding {
  /** 模板里写的引用（字段 id 或字段名）。 */
  ref: string;
  /** 所在区块 id，界面据此定位。 */
  sectionId: string;
  /** 人话的位置描述，如「信息网格 · 客户名称」。 */
  where: string;
  /** 命中的表字段名；未命中为 null。 */
  resolved: string | null;
}

export interface BindingReport {
  bindings: FieldBinding[];
  bound: number;
  unmatched: FieldBinding[];
}

const SECTION_LABEL: Record<ContractSection['type'], string> = {
  header: '标题/抬头',
  info_grid: '信息网格',
  detail_table: '明细表格',
  free_table: '自由表格',
  text_block: '文章区块',
  signature: '签章区',
  attachment: '附件图片',
  code: '二维码/条码',
  rich_text: '富文本',
  divider: '分隔',
  columns: '分栏',
};

/** 从一段文本里挖出 {{占位符}}，去掉 |大写 这类修饰符。 */
function refsInText(text: string | null | undefined): string[] {
  if (!text) return [];
  const out: string[] = [];
  for (const m of text.matchAll(/\{\{([^}]+)\}\}/g)) {
    const raw = m[1];
    const pipe = raw.lastIndexOf('|');
    const ref = (pipe >= 0 ? raw.slice(0, pipe) : raw).trim();
    if (ref) out.push(ref);
  }
  return out;
}

export function collectBindings(
  template: ContractTemplate,
  fieldMetas: Pick<FieldMeta, 'id' | 'name'>[],
): BindingReport {
  const raw: Array<{ ref: string; sectionId: string; where: string }> = [];
  const push = (ref: string, sectionId: string, where: string) => {
    if (ref) raw.push({ ref, sectionId, where });
  };

  for (const s of walkSections(template.sections)) {
    const label = SECTION_LABEL[s.type] ?? s.type;
    switch (s.type) {
      case 'header':
      case 'text_block':
        for (const r of refsInText(s.content)) push(r, s.id, label);
        break;
      case 'info_grid':
        for (const item of s.items) {
          if (item.fieldId) push(item.fieldId, s.id, `${label} · ${item.label || item.fieldId}`);
          for (const r of refsInText(item.staticValue)) push(r, s.id, `${label} · ${item.label}`);
        }
        break;
      case 'signature':
        for (const p of s.parties) {
          for (const row of p.rows) {
            if (row.fieldId) push(row.fieldId, s.id, `${label} · ${p.label} · ${row.label}`);
            for (const r of refsInText(row.staticValue)) push(r, s.id, `${label} · ${p.label}`);
          }
        }
        break;
      case 'free_table':
        for (const row of s.rows) {
          for (const cell of row.cells) {
            for (const r of refsInText(cell.content)) push(r, s.id, label);
          }
        }
        break;
      case 'detail_table':
        // 关联字段绑在主表上，列绑在关联表上（列在这里不查，属于另一张表）
        push(s.linkFieldId, s.id, `${label} · 关联字段`);
        break;
      case 'attachment':
      case 'code':
      case 'rich_text':
        if (s.fieldId) push(s.fieldId, s.id, label);
        break;
      default:
        break;
    }
  }
  // 条件显隐引用的字段也算
  for (const [sectionId, rule] of Object.entries(template.visibility ?? {})) {
    if (rule?.fieldId) push(rule.fieldId, sectionId, '条件显示');
  }

  // 同一个引用在同一个区块里出现多次只算一处，免得数字虚高
  const seen = new Set<string>();
  const bindings: FieldBinding[] = [];
  for (const r of raw) {
    const key = `${r.sectionId}|${r.ref}|${r.where}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const hit = findFieldByRef(fieldMetas, r.ref);
    bindings.push({ ...r, resolved: hit ? hit.name : null });
  }
  const unmatched = bindings.filter((b) => b.resolved === null);
  return { bindings, bound: bindings.length - unmatched.length, unmatched };
}
